import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { SubscriptionBadge } from './SubscriptionBadge';
import { LogOut, Menu, Settings, Zap } from 'lucide-react';
import { PERMISSIONS } from '../constants/permissions';

// ─── Props ────────────────────────────────────────────────────────────────────

interface FloatingHeaderProps {
  onMenuClick: () => void;
  sidebarCollapsed?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FloatingHeader: React.FC<FloatingHeaderProps> = ({ onMenuClick, sidebarCollapsed = false }) => {
  const navigate = useNavigate();
  const { user, logout, hasPermission, isOwner } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // User initials (up to 2 chars)
  const initials = (() => {
    const name = user?.name || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts[0]?.length > 0) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (user?.email?.slice(0, 2) || '??').toUpperCase();
  })();

  const canViewConfig = hasPermission(PERMISSIONS.CONFIGURACION.VER);
  const owner = isOwner();

  return (
    <header
      className={[
        /* Position — floating pill */
        'fixed top-3 z-30',
        'right-4 left-4',
        /* On desktop, offset past the sidebar — transitions with it */
        sidebarCollapsed
          ? 'lg:left-[calc(4.5rem+1rem)]'
          : 'lg:left-[calc(16rem+1rem)]',
        'transition-[left] duration-300 ease-in-out',
        /* Layout */
        'flex items-center h-16 px-5 gap-3',
        /* Glass surface */
        'bg-card/85 backdrop-blur-md',
        'border border-border/60',
        'rounded-2xl',
        'shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)]',
      ].join(' ')}
    >
      {/* ── Mobile hamburger ─────────────────────────────── */}
      <button
        type="button"
        onClick={onMenuClick}
        className="flex items-center justify-center flex-shrink-0 w-8 h-8 transition-colors rounded-lg lg:hidden text-muted-foreground hover:bg-accent hover:text-foreground"
        aria-label="Abrir menú"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* ── LEFT: Nav links ───────────────────────── */}
      <div className="flex items-center flex-shrink-0 gap-1">

        {owner && (
          <Link
            to="/billing"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors font-medium"
          >
            <Zap className="w-3.5 h-3.5" />
            Suscripción
          </Link>
        )}

        {/* Nav links */}
        {canViewConfig && (
          <Link
            to="/config"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors font-medium"
          >
            <Settings className="w-3.5 h-3.5" />
            Configuración
          </Link>
        )}

        {/* Subscription status */}
        <SubscriptionBadge />
      </div>

      {/* ── SPACER ────────────────────────────────────────── */}
      <div className="flex-1" />

      {/* ── RIGHT: User info + Logout + Theme ───────────── */}
      <div className="flex items-center flex-shrink-0 gap-2">
        {/* Avatar + name */}
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 hover:bg-accent transition-colors group"
          aria-label="Ir a mi perfil"
          title="Mi perfil"
        >
          {/* Name + role */}
          <div className="hidden min-w-0 text-right md:block">
            <p className="text-sm font-semibold text-foreground leading-tight truncate max-w-[140px]">
              {user?.name || user?.email || 'Usuario'}
            </p>
            {user?.roleName && (
              <p className="text-[11px] text-muted-foreground leading-tight truncate max-w-[140px]">
                {user.roleName === 'OWNER' ? 'Administrador' : user.roleName}
              </p>
            )}
          </div>
          {/* Avatar circle with initials */}
          <div
            className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-xs font-bold text-white rounded-full shadow-sm"
            style={{
              background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(213 55% 28%))',
            }}
          >
            {initials}
          </div>
        </button>

        {/* Divider */}
        <div className="flex-shrink-0 hidden w-px h-5 md:block bg-border" />

        {/* Theme toggle */}
        <ThemeToggle compact />

        {/* Logout button */}
        <button
          type="button"
          onClick={handleLogout}
          className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/8 transition-colors font-medium flex-shrink-0"
          aria-label="Cerrar sesión"
          title="Cerrar sesión"
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden lg:inline">Salir</span>
        </button>

      </div>
    </header>
  );
};
